<template>
  <div class="auth-wrapper">
    <div class="full-page">
      <div class="content">
        <div class="container">
          <div class="login-container">
            <div class="login-card">
              <div class="card-header">
                <div class="logo-container">
                  <img src="/img/logo-sia.png" alt="Logo" style="width: 100%;">
                </div>
              </div>
              
              <div class="card-body">
                <div v-if="error" class="alert alert-danger">
                  {{ error }}
                </div>
                
                <form @submit.prevent="handleLogin">
                  <div class="form-group">
                    <label>Usuario</label>
                    <input 
                      type="text" 
                      class="form-control" 
                      placeholder="Usuario" 
                      v-model="form.username"
                      :class="{ 'is-invalid': validationErrors.username }"
                    >
                    <div v-if="validationErrors.username" class="invalid-feedback">
                      {{ validationErrors.username[0] }}
                    </div>
                  </div>
                  
                  <div class="form-group">
                    <label>Contraseña</label>
                    <input 
                      type="password" 
                      class="form-control" 
                      placeholder="Contraseña" 
                      v-model="form.password"
                      :class="{ 'is-invalid': validationErrors.password }"
                    >
                    <div v-if="validationErrors.password" class="invalid-feedback">
                      {{ validationErrors.password[0] }}
                    </div>
                  </div>
                  
                  <div class="form-group">
                    <label>Código de seguridad</label>
                    <div class="captcha-container">
                      <div v-html="captchaImage" class="captcha-image"></div>
                      <button 
                        type="button" 
                        class="btn btn-refresh" 
                        @click="refreshCaptcha"
                      >
                        <i class="fa fa-undo"></i>
                      </button>
                    </div>
                    <input 
                      type="text" 
                      class="form-control mt-2" 
                      placeholder="Ingrese el código de seguridad" 
                      v-model="form.captcha"
                      :class="{ 'is-invalid': validationErrors.captcha }"
                    >
                    <div v-if="validationErrors.captcha" class="invalid-feedback">
                      {{ validationErrors.captcha[0] }}
                    </div>
                  </div>
                  
                  <div class="text-center">
                    <button type="submit" class="btn btn-login" :disabled="loading">
                      <span v-if="loading">
                        <i class="nc-icon nc-spin nc-refresh-02"></i> Iniciando sesión...
                      </span>
                      <span v-else>Iniciar Sesión</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <footer class="footer">
        <div class="container">
          <div class="copyright text-center">
            &copy; {{ new Date().getFullYear() }} Sistema de Tramites DDE-Beni
          </div>
        </div>
      </footer>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';

export default {
  setup() {
    const authStore = useAuthStore();
    const form = ref({
      username: '',
      password: '',
      captcha: ''
    });
    const captchaImage = ref('');
    const loading = ref(false);
    const error = ref('');
    const validationErrors = ref({});

    const refreshCaptcha = async () => {
      try {
        const response = await axios.get('/refresh-captcha');
        captchaImage.value = response.data.captcha;
      } catch (err) {
        console.error('Error al cargar captcha:', err);
      }
    };

    const handleLogin = async () => {
      loading.value = true;
      error.value = '';
      validationErrors.value = {};

      try {
        await authStore.login(form.value);
      } catch (err) {
        if (err.response?.status === 422) {
          validationErrors.value = err.response.data.errors;
        } else {
          error.value = err.response?.data?.message || 'Error al iniciar sesión. Intente nuevamente.';
        }
        refreshCaptcha();
      } finally {
        loading.value = false;
        form.value.captcha = '';
      }
    };

    onMounted(() => {
      refreshCaptcha();
    });

    return {
      form,
      captchaImage,
      loading,
      error,
      validationErrors,
      refreshCaptcha,
      handleLogin
    };
  }
};
</script>

<style scoped>
.auth-wrapper {
  min-height: 100vh;
  background-color: #f8f9fa;
  display: flex;
  flex-direction: column;
}

.full-page {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
  background-color: #f5f7fa;
}

.login-container {
  width: 100%;
  max-width: 450px;
  margin: 0 auto;
}

.login-card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border: 1px solid #e1e5eb;
}

.card-header {
  background-color: #f0f1f7;
  padding: 20px;
  text-align: center;
}

.logo-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.logo-image {
  width: 60px;
  height: 60px;
  margin-bottom: 15px;
}

.header {
  color: white;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 500;
}

.card-body {
  padding: 30px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
}

.form-control {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #00B5DB;
  box-shadow: 0 0 0 2px rgba(0, 181, 219, 0.2);
  outline: none;
}

.is-invalid {
  border-color: #f44336;
}

.invalid-feedback {
  color: #f44336;
  font-size: 0.85rem;
  margin-top: 5px;
}

.captcha-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.captcha-image {
  flex-grow: 1;
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 5px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-refresh {
  background: transparent;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 10px;
  color: #555;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-refresh:hover {
  background: #f5f5f5;
  color: #2D2E83;
}

.btn-login {
  width: 100%;
  padding: 12px;
  background-color: #FF6B35;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 10px;
}

.btn-login:hover {
  background-color: #0069a7;
}

.btn-login:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
  padding: 10px 15px;
  border-radius: 4px;
  margin-bottom: 20px;
  border: 1px solid #f5c6cb;
}

.footer {
  padding: 20px 0;
  background-color: #4A5568;
  color: white;
}

.copyright {
  font-size: 0.9rem;
  color: white;
}

/* Animaciones */
.nc-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 576px) {
  .content {
    padding: 15px;
  }
  
  .login-card {
    box-shadow: none;
    border: none;
  }
  
  .card-body {
    padding: 20px;
  }
  
  .header {
    font-size: 1.3rem;
  }
}
</style>